import jsPDF from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import type { CutierLead } from '../../store/useCutierStore';
import type { CutierInvoiceProfile } from '../../types/cutierInvoiceProfile';
import { generateZugferdXml } from '../pe-papers/zugferdGenerator';
import { assessCutierInvoiceProfile, buildCutierZugferdPayload } from './cutierInvoiceProfileUtils';
import { sumLeadUnits } from './cutierLeadUtils';
import { RAMIN_DELL_LOGO_SRC } from '../../constants/brandAssets';

export interface GenerateCutierInvoiceParams {
  monthKey: string;
  leads: CutierLead[];
  profile: CutierInvoiceProfile;
  invoiceNumber?: string;
  invoiceDate?: string;
  rateGross?: number;
  isTest?: boolean;
  testParticipantCount?: number;
}

export interface CutierInvoicePdfResult {
  pdfBytes: Uint8Array;
  invoiceNumber: string;
  fileName: string;
  base64PDF: string;
  grossTotal: number;
  netTotal: number;
  vatTotal: number;
  unitCount: number;
}

const euro = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;

/** Logo kompakt als JPEG — volle PNG-Auflösung bläht die E-Rechnung auf >1 MB auf. */
const loadLogoDataUrl = (src: string): Promise<{ dataUrl: string; w: number; h: number; format: 'JPEG' | 'PNG' } | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const maxW = 480;
        const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.fillStyle = '#111111';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.72), w, h, format: 'JPEG' });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

function uint8ToDataUri(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return `data:application/pdf;base64,${btoa(binary)}`;
}

function triggerPdfDownload(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function buildCutierInvoicePdf(
  params: GenerateCutierInvoiceParams
): Promise<CutierInvoicePdfResult> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const profile = params.profile;
  const pageWidth = 210;
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let currentY = 25;

  const unitGross = params.rateGross && params.rateGross > 0 ? params.rateGross : 50;
  const count = params.isTest ? (params.testParticipantCount ?? 3) : sumLeadUnits(params.leads);
  const grossTotal = count * unitGross;
  const netTotal = grossTotal / 1.19;
  const vatTotal = grossTotal - netTotal;

  const invoiceNumber =
    params.invoiceNumber ||
    (params.isTest
      ? `RD-CUT-TEST-${params.monthKey.replace('-', '')}`
      : `RD-CUT-${params.monthKey.replace('-', '')}`);
  const invoiceDate = params.invoiceDate || new Date().toLocaleDateString('de-DE');
  const fileName = `Rechnung_${invoiceNumber}.pdf`;
  const logo = await loadLogoDataUrl(RAMIN_DELL_LOGO_SRC);

  if (logo) {
    const logoH = 28;
    const logoW = Math.min(72, (logo.w / logo.h) * logoH);
    const platePad = 3;
    const plateX = pageWidth - margin - logoW - platePad * 2;
    const plateY = currentY - 4;
    doc.setFillColor(17, 17, 17);
    doc.setDrawColor(230, 194, 153);
    doc.setLineWidth(0.3);
    doc.roundedRect(plateX, plateY, logoW + platePad * 2, logoH + platePad * 2, 2, 2, 'FD');
    doc.addImage(logo.dataUrl, logo.format, plateX + platePad, plateY + platePad, logoW, logoH);
    currentY += logoH + platePad * 2 + 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(profile.displayName || 'Rechnungssteller', pageWidth - margin, currentY, { align: 'right' });
  currentY += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(profile.street || '', pageWidth - margin, currentY, { align: 'right' });
  currentY += 4;
  doc.text(`${profile.zip || ''} ${profile.city || ''}`.trim(), pageWidth - margin, currentY, {
    align: 'right',
  });
  currentY += 4;
  if (profile.email) {
    doc.text(profile.email, pageWidth - margin, currentY, { align: 'right' });
    currentY += 4;
  }
  if (profile.phone) {
    doc.text(profile.phone, pageWidth - margin, currentY, { align: 'right' });
    currentY += 4;
  }
  currentY += 8;

  const senderLine = [profile.displayName, profile.street, `${profile.zip} ${profile.city}`.trim()]
    .filter(Boolean)
    .join(' • ');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(senderLine, margin, currentY);
  currentY += 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  doc.text(profile.buyerCompanyName || 'Empfänger', margin, currentY);
  currentY += 4.5;
  if (profile.buyerContactPerson) {
    doc.setFont('helvetica', 'normal');
    doc.text(`z. Hd. ${profile.buyerContactPerson}`, margin, currentY);
    currentY += 4;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(profile.buyerStreet || '', margin, currentY);
  currentY += 4;
  doc.text(`${profile.buyerZip || ''} ${profile.buyerCity || ''}`.trim(), margin, currentY);
  currentY += 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text(params.isTest ? 'RECHNUNG (TEST)' : 'RECHNUNG', margin, currentY);
  currentY += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(51, 65, 85);
  doc.text(`Rechnungs-Nr.: ${invoiceNumber}`, margin, currentY);
  doc.text(`Datum: ${invoiceDate}`, margin + 65, currentY);
  doc.text(`Leistungszeitraum: ${params.monthKey}`, margin + 115, currentY);
  currentY += 8;

  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 8;

  doc.setFillColor(241, 245, 249);
  doc.rect(margin, currentY, contentWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(30, 41, 59);
  doc.text('Pos.', margin + 2, currentY + 4.8);
  doc.text('Beschreibung', margin + 14, currentY + 4.8);
  doc.text('Anzahl', margin + 95, currentY + 4.8, { align: 'right' });
  doc.text('Einzel (Netto)', margin + 130, currentY + 4.8, { align: 'right' });
  doc.text('Gesamt (Netto)', pageWidth - margin - 2, currentY + 4.8, { align: 'right' });
  currentY += 10;

  const unitNet = unitGross / 1.19;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(51, 65, 85);
  doc.text('1', margin + 2, currentY);
  doc.text(`Seminar-Vergütung Online-Schulung (${params.monthKey})`, margin + 14, currentY);
  doc.text(`${count}`, margin + 95, currentY, { align: 'right' });
  doc.text(euro(unitNet), margin + 130, currentY, { align: 'right' });
  doc.text(euro(netTotal), pageWidth - margin - 2, currentY, { align: 'right' });
  currentY += 8;

  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 6;

  const valuesX = pageWidth - margin - 2;
  const labelsX = pageWidth - margin - 48;
  doc.setFontSize(9.5);
  doc.text('Nettobetrag:', labelsX, currentY, { align: 'right' });
  doc.text(euro(netTotal), valuesX, currentY, { align: 'right' });
  currentY += 4.5;
  doc.text('zzgl. 19% MwSt.:', labelsX, currentY, { align: 'right' });
  doc.text(euro(vatTotal), valuesX, currentY, { align: 'right' });
  currentY += 5.5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  doc.text('Gesamtbetrag (Brutto):', labelsX, currentY, { align: 'right' });
  doc.text(euro(grossTotal), valuesX, currentY, { align: 'right' });
  currentY += 14;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(71, 85, 105);
  const payDays = profile.defaultPaymentTermsDays || 14;
  doc.text(`Zahlbar innerhalb von ${payDays} Tagen ohne Abzug.`, margin, currentY);
  currentY += 4;
  if (profile.iban) {
    doc.text(
      `Bank: ${profile.bankName || '—'} · IBAN: ${profile.iban}${profile.bic ? ` · BIC: ${profile.bic}` : ''}`,
      margin,
      currentY
    );
    currentY += 4;
  }
  if (profile.taxNumber || profile.vatId) {
    doc.text(
      `Steuer-Nr.: ${profile.taxNumber || '—'}${profile.vatId ? ` · USt-IdNr.: ${profile.vatId}` : ''}`,
      margin,
      currentY
    );
    currentY += 4;
  }
  doc.text(
    params.isTest
      ? `TESTMODUS: ${count} Muster-Teilnehmer im Monat ${params.monthKey} — keine echten Adressen.`
      : `Leistungsnachweis: ${count} registrierte Seminar-Teilnehmer im Monat ${params.monthKey}.`,
    margin,
    currentY
  );
  currentY += 12;

  if (!params.isTest && params.leads.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42);
    doc.text('Übersicht der erfassten Teilnehmer:', margin, currentY);
    currentY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    params.leads.slice(0, 15).forEach((lead, i) => {
      const qty = lead.orderCount > 1 ? ` (${lead.orderCount}×)` : '';
      const line = `${i + 1}. ${lead.email}${lead.name ? ` (${lead.name})` : ''}${qty} — ${lead.createdAt.split('T')[0]}`;
      doc.text(line, margin + 2, currentY);
      currentY += 3.8;
    });
    if (params.leads.length > 15) {
      doc.text(`... und ${params.leads.length - 15} weitere Kunden.`, margin + 2, currentY);
    }
  }

  const pdfBytes = new Uint8Array(doc.output('arraybuffer'));
  const base64PDF = doc.output('datauristring');

  return {
    pdfBytes,
    invoiceNumber,
    fileName,
    base64PDF,
    grossTotal,
    netTotal,
    vatTotal,
    unitCount: count,
  };
}

export async function buildCutierInvoiceHybridPdf(
  params: GenerateCutierInvoiceParams
): Promise<CutierInvoicePdfResult> {
  const base = await buildCutierInvoicePdf(params);
  const invoiceDateIso = new Date().toISOString().split('T')[0];
  const zugferd = buildCutierZugferdPayload({
    profile: params.profile,
    monthKey: params.monthKey,
    invoiceNumber: base.invoiceNumber,
    invoiceDateIso,
    unitCount: base.unitCount,
    unitGross: params.rateGross && params.rateGross > 0 ? params.rateGross : 50,
    netTotal: base.netTotal,
    grossTotal: base.grossTotal,
    vatTotal: base.vatTotal,
  });
  const xmlContent = generateZugferdXml(zugferd);
  const xmlBytes = new TextEncoder().encode(xmlContent);
  const pdfDoc = await PDFDocument.load(base.pdfBytes);
  await pdfDoc.attach(xmlBytes, 'factur-x.xml', {
    mimeType: 'text/xml',
    description: 'Factur-X / ZUGFeRD 2.1 Invoice XML',
    creationDate: new Date(),
    modificationDate: new Date(),
  });
  const hybridBytes = new Uint8Array(await pdfDoc.save());
  const hybridBase64 = uint8ToDataUri(hybridBytes);
  return {
    ...base,
    pdfBytes: hybridBytes,
    base64PDF: hybridBase64,
    fileName: base.fileName.replace('.pdf', '_E-Rechnung.pdf'),
  };
}

export async function downloadCutierInvoicePdf(
  params: GenerateCutierInvoiceParams,
  hybrid = false
): Promise<CutierInvoicePdfResult> {
  if (!params.isTest && !assessCutierInvoiceProfile(params.profile).complete) {
    throw new Error('Rechnungsprofil unvollständig — IBAN und Steuerdaten fehlen.');
  }
  const result = hybrid
    ? await buildCutierInvoiceHybridPdf(params)
    : await buildCutierInvoicePdf(params);
  triggerPdfDownload(result.pdfBytes, result.fileName);
  return result;
}

/** @deprecated Nutze downloadCutierInvoicePdf */
export async function generateCutierInvoicePdf(params: GenerateCutierInvoiceParams): Promise<void> {
  await downloadCutierInvoicePdf(params, false);
}
